import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { dashboardAPI } from '@/lib/api';
import { PrimeSecteurWidget } from '@/components/dashboard/PrimeSecteurWidget';
import {
    Trophy, Loader2, ChevronDown, ChevronUp, Users, Crown, Gift,
} from 'lucide-react';

const fmtEur = (v) => `${(Number(v) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
const MONTH_LABEL = (ym) => {
    if (!ym) return '';
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};
const STORAGE_KEY = 'valnexo_prime_admin_expanded';

function SectorRow({ sector, month }) {
    const [open, setOpen] = useState(false);
    if (open) {
        return (
            <div data-testid={`prime-admin-sector-${sector.vp_id}`}>
                <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="text-xs text-amber-700 hover:text-amber-800 font-medium mb-1 flex items-center gap-1"
                    data-testid={`prime-admin-collapse-${sector.vp_id}`}
                >
                    <ChevronUp className="w-3.5 h-3.5" /> Réduire {sector.vp_name}
                </button>
                <PrimeSecteurWidget vpId={sector.vp_id} sectorName={sector.vp_name} defaultExpanded />
            </div>
        );
    }
    return (
        <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full text-left rounded-xl border border-slate-200 bg-white hover:border-amber-300 hover:bg-amber-50/40 transition-colors px-3 py-2.5 flex items-center gap-3"
            data-testid={`prime-admin-sector-${sector.vp_id}`}
        >
            <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-800 truncate">{sector.vp_name}</p>
                <p className="text-[11px] text-slate-400 flex items-center gap-1">
                    <Users className="w-3 h-3" /> {sector.apporteurs_count} apporteur{sector.apporteurs_count > 1 ? 's' : ''} · {sector.won_count} gagné{sector.won_count > 1 ? 's' : ''}
                </p>
            </div>
            <div className="text-right shrink-0">
                <p className="text-sm font-bold text-slate-900">{fmtEur(sector.ca_ep_total)}</p>
                <p className="text-[10px] text-slate-400">CA du mois</p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-0.5">
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] py-0">
                    Palier {sector.palier > 0 ? fmtEur(sector.palier) : '—'}
                </Badge>
                <span className="text-[10px] text-slate-500 flex items-center gap-1.5">
                    <span className="text-emerald-600 flex items-center gap-0.5"><Gift className="w-2.5 h-2.5" />{fmtEur(sector.prime_apporteurs_total)}</span>
                    <span className="text-purple-600 flex items-center gap-0.5"><Crown className="w-2.5 h-2.5" />{fmtEur(sector.prime_responsable_total)}</span>
                </span>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-300 shrink-0" />
        </button>
    );
}

export function PrimeSecteurAdminOverview() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [expanded, setExpanded] = useState(() => {
        try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
    });

    const toggle = () => {
        setExpanded((prev) => {
            const next = !prev;
            try { localStorage.setItem(STORAGE_KEY, next ? '1' : '0'); } catch { /* noop */ }
            return next;
        });
    };

    useEffect(() => {
        if (!expanded || data !== null || loading) return;
        setLoading(true);
        setError(false);
        dashboardAPI.getPrimeSecteurOverview({})
            .then((res) => setData(res.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [expanded, data, loading]);

    const sectors = data?.sectors || [];
    const totals = data?.totals;

    return (
        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 overflow-hidden" data-testid="prime-secteur-admin-overview">
            <button
                type="button"
                onClick={toggle}
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-amber-50/60 transition-colors"
                data-testid="prime-admin-toggle"
                aria-expanded={expanded}
            >
                <div className="p-2 rounded-full bg-amber-100 shrink-0">
                    <Trophy className="w-5 h-5 text-amber-500" />
                </div>
                <div className="min-w-0 flex-1">
                    <span className="text-sm font-semibold text-slate-800">Primes de secteur — Courtage (tous les secteurs)</span>
                    <p className="text-xs text-slate-500 mt-0.5">
                        {totals ? `${totals.sectors_count} secteur${totals.sectors_count > 1 ? 's' : ''} · CA total ${fmtEur(totals.ca_ep_total)} (${MONTH_LABEL(data.month)})` : 'Vue d\u2019ensemble par responsable — cliquez pour ouvrir'}
                    </p>
                </div>
                <span className="shrink-0 text-slate-400">
                    {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </span>
            </button>

            {expanded && (
                <CardContent className="pt-0 pb-5 border-t border-amber-100">
                    {loading ? (
                        <div className="py-6 flex items-center justify-center gap-2 text-slate-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin" /> Chargement des secteurs…
                        </div>
                    ) : error ? (
                        <p className="py-4 text-sm text-rose-500">Erreur lors du chargement.</p>
                    ) : sectors.length === 0 ? (
                        <p className="py-4 text-sm text-slate-400">Aucun secteur avec des apporteurs.</p>
                    ) : (
                        <div className="space-y-2 pt-3" data-testid="prime-admin-sectors">
                            {sectors.map((s) => (
                                <SectorRow key={s.vp_id} sector={s} month={data.month} />
                            ))}
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    );
}

export default PrimeSecteurAdminOverview;
